/*
 * Данные хранилища из Kubernetes API кластера (плагин Kubernetes, прокси бэкенда) и
 * Prometheus кластера: для PostgreSQL — Cluster CloudNativePG, его Database и DatabaseRole
 * (базы и пользователи описаны только в IaC) и поды инстансов, размеры баз — по метрикам;
 * для Kafka — Kafka, KafkaNodePool, KafkaTopic Strimzi, поды узлов, топики по метрикам.
 * Кластер и имя хранилища — из аннотаций сущности.
 */
import { createContext, ReactNode, useContext } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import useAsync, { AsyncState } from 'react-use/esm/useAsync';
import {
  DATASTORE_CLUSTER_ANNOTATION,
  annotation,
  datastoreNameOf,
} from '../../../lib/viva';
import {
  CnpgCluster,
  CnpgDatabase,
  CnpgDatabaseRole,
  DATASTORES_NAMESPACE,
  DatastorePod,
  KafkaNode,
  KafkaPool,
  KafkaSummary,
  KafkaTopic,
  PgDatabase,
  PgInstance,
  PgUser,
  PostgresSummary,
  StrimziKafka,
  StrimziNodePool,
  StrimziTopic,
  kafkaNodes,
  kafkaPools,
  kafkaSummary,
  kafkaTopics,
  pgDatabases,
  pgInstances,
  pgSummary,
  pgUnmanagedDatabases,
  pgUsers,
} from '../../../lib/datastores';
import { clusterClient } from '../../../lib/k8sProxy';
import {
  PromSample,
  kafkaTopicPartitionsQuery,
  postgresDatabasesQuery,
  queryInstant,
} from '../../../lib/prometheus';
import { CellState, settle } from '../cells';

export type PostgresData = {
  available: boolean;
  summary: PostgresSummary;
  instances: PgInstance[];
  users: PgUser[];
  databases: PgDatabase[];
  /** Размеры баз из Prometheus (имя -> байты); ошибка — Prometheus недоступен. */
  sizes: CellState<Map<string, number>>;
  /** Базы, которые есть в PostgreSQL, но не описаны в IaC (заведены руками). */
  unmanaged: string[];
};

export type KafkaData = {
  available: boolean;
  summary: KafkaSummary;
  pools: KafkaPool[];
  nodes: KafkaNode[];
  topics: KafkaTopic[];
  /** Ошибка метрик топиков — в таблице только KafkaTopic. */
  metricsError?: Error;
};

const ns = DATASTORES_NAMESPACE;

/** Клиент API кластера через прокси плагина; undefined — кластер не подключён. */
const useClusterClient = (entity: Entity) => {
  const kubernetesApi = useApi(kubernetesApiRef);
  const clusterName = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
  return {
    clusterName,
    connect: () => clusterClient(kubernetesApi, clusterName),
  };
};

const emptyList = <T,>(): { items: T[] } => ({ items: [] });

export const usePostgresData = (entity: Entity): AsyncState<PostgresData> => {
  const client = useClusterClient(entity);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const name = datastoreNameOf(entity);
  const { clusterName } = client;

  return useAsync(async (): Promise<PostgresData> => {
    const get = await client.connect();
    const empty: PostgresData = {
      available: false,
      summary: pgSummary(undefined),
      instances: [],
      users: [],
      databases: [],
      sizes: { loading: false, value: new Map() },
      unmanaged: [],
    };
    if (!get) return empty;
    // Database/DatabaseRole — CRD новее Cluster: без них (старый оператор) список пустой
    const [cluster, pods, dbs, roles, samples] = await Promise.all([
      get<CnpgCluster>(
        `/apis/postgresql.cnpg.io/v1/namespaces/${ns}/clusters/${name}`,
      ),
      get<{ items: DatastorePod[] }>(
        `/api/v1/namespaces/${ns}/pods?labelSelector=${encodeURIComponent(
          `cnpg.io/cluster=${name}`,
        )}`,
      ),
      get<{ items: CnpgDatabase[] }>(
        `/apis/postgresql.cnpg.io/v1/namespaces/${ns}/databases`,
      ).catch(emptyList<CnpgDatabase>),
      get<{ items: CnpgDatabaseRole[] }>(
        `/apis/postgresql.cnpg.io/v1/namespaces/${ns}/databaseroles`,
      ).catch(emptyList<CnpgDatabaseRole>),
      settle<PromSample[]>(
        queryInstant(
          { discoveryApi, fetchApi },
          clusterName as string,
          postgresDatabasesQuery(name),
        ),
      ),
    ]);
    const sizes: CellState<Map<string, number>> = samples.error
      ? { loading: false, error: samples.error }
      : {
          loading: false,
          value: new Map(
            (samples.value ?? []).map(s => [s.metric.datname, s.value]),
          ),
        };
    const databases = pgDatabases(name, cluster, dbs.items);
    return {
      available: true,
      summary: pgSummary(cluster),
      instances: pgInstances(cluster, pods.items),
      users: pgUsers(name, cluster, roles.items),
      databases,
      sizes,
      unmanaged: pgUnmanagedDatabases(databases, sizes.value?.keys() ?? []),
    };
  }, [clusterName, name]);
};

export const useKafkaData = (entity: Entity): AsyncState<KafkaData> => {
  const client = useClusterClient(entity);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const name = datastoreNameOf(entity);
  const { clusterName } = client;

  return useAsync(async (): Promise<KafkaData> => {
    const get = await client.connect();
    if (!get) {
      return {
        available: false,
        summary: kafkaSummary(undefined),
        pools: [],
        nodes: [],
        topics: [],
      };
    }
    const selector = encodeURIComponent(`strimzi.io/cluster=${name}`);
    const [kafka, pools, pods, topics, metrics] = await Promise.all([
      get<StrimziKafka>(
        `/apis/kafka.strimzi.io/v1/namespaces/${ns}/kafkas/${name}`,
      ),
      get<{ items: StrimziNodePool[] }>(
        `/apis/kafka.strimzi.io/v1/namespaces/${ns}/kafkanodepools?labelSelector=${selector}`,
      ),
      get<{ items: DatastorePod[] }>(
        `/api/v1/namespaces/${ns}/pods?labelSelector=${selector}`,
      ),
      get<{ items: StrimziTopic[] }>(
        `/apis/kafka.strimzi.io/v1/namespaces/${ns}/kafkatopics?labelSelector=${selector}`,
      ),
      settle<PromSample[]>(
        queryInstant(
          { discoveryApi, fetchApi },
          clusterName as string,
          kafkaTopicPartitionsQuery(name),
        ),
      ),
    ]);
    return {
      available: true,
      summary: kafkaSummary(kafka),
      pools: kafkaPools(pools.items),
      nodes: kafkaNodes(pods.items),
      topics: kafkaTopics(
        topics.items,
        (metrics.value ?? []).map(s => ({
          topic: s.metric.topic,
          partitions: s.value,
        })),
      ),
      metricsError: metrics.error,
    };
  }, [clusterName, name]);
};

/* Провайдеры на страницу: один запрос к API на обе карточки. */
const PostgresCtx = createContext<AsyncState<PostgresData> | undefined>(
  undefined,
);
const KafkaCtx = createContext<AsyncState<KafkaData> | undefined>(undefined);

export const PostgresDataProvider = ({ children }: { children: ReactNode }) => {
  const { entity } = useEntity();
  const state = usePostgresData(entity);
  return <PostgresCtx.Provider value={state}>{children}</PostgresCtx.Provider>;
};

export const KafkaDataProvider = ({ children }: { children: ReactNode }) => {
  const { entity } = useEntity();
  const state = useKafkaData(entity);
  return <KafkaCtx.Provider value={state}>{children}</KafkaCtx.Provider>;
};

export const usePostgresState = (): AsyncState<PostgresData> => {
  const state = useContext(PostgresCtx);
  if (!state) throw new Error('usePostgresState вне PostgresDataProvider');
  return state;
};

export const useKafkaState = (): AsyncState<KafkaData> => {
  const state = useContext(KafkaCtx);
  if (!state) throw new Error('useKafkaState вне KafkaDataProvider');
  return state;
};

/** Заглушка, когда кластер хранилища не описан в плагине Kubernetes. */
export const NOT_CONNECTED =
  'Данные читаются из API кластера через k8s-proxy GitLab Agent: у сущности нужна аннотация viva.company.com/cluster, а кластер должен быть описан для плагина Kubernetes (агент в проекте viva/clusters, user_access для группы viva).';
